import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAdmin } from '../../components/Admin/AdminContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Image as ImageIcon, Search, Trash2, Edit, MapPin, 
  Car, Calendar, User, Eye, ArrowRight, X, Save,
  Sun, Cloud, Compass, Clock, RefreshCw, ExternalLink, Loader2
} from 'lucide-react';

export default function AdminPhotos() {
  const { adminAPI } = useAdmin();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [photoTypeFilter, setPhotoTypeFilter] = useState('all');
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveTargetSpot, setMoveTargetSpot] = useState('');
  const [spots, setSpots] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [totalPhotos, setTotalPhotos] = useState(0);

  const observerRef = useRef();
  const loadMoreRef = useRef();
  const photosPerPage = 30;

  // Reset et rechargement initial quand les filtres changent
  useEffect(() => {
    setPhotos([]);
    setHasMore(true);
    loadPhotos(true);
    loadSpots();
  }, [searchTerm, photoTypeFilter]);

  // Intersection Observer pour scroll infini
  useEffect(() => {
    const options = { root: null, rootMargin: '200px', threshold: 0.1 };

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
        loadMorePhotos();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [hasMore, loadingMore, loading, photos.length]);

  const loadPhotos = async (reset = false) => {
    try {
      setLoading(true);
      const params = {
        skip: 0,
        limit: photosPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(photoTypeFilter !== 'all' && { photo_type: photoTypeFilter })
      };
      
      const data = await adminAPI.getPhotos(params);
      setPhotos(data.photos || []);
      setTotalPhotos(data.total || 0);
      setHasMore((data.photos || []).length === photosPerPage);
    } catch (error) {
      console.error('Failed to load photos:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMorePhotos = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const params = {
        skip: photos.length,
        limit: photosPerPage,
        ...(searchTerm && { search: searchTerm }),
        ...(photoTypeFilter !== 'all' && { photo_type: photoTypeFilter })
      };
      
      const data = await adminAPI.getPhotos(params);
      const newPhotos = data.photos || [];
      setPhotos(prev => [...prev, ...newPhotos]);
      setHasMore(newPhotos.length === photosPerPage);
    } catch (error) {
      console.error('Failed to load more photos:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const loadSpots = async () => {
    try {
      const data = await adminAPI.getSpots({ limit: 100 });
      setSpots(data || []);
    } catch (error) {
      console.error('Failed to load spots:', error);
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!confirm(`Êtes-vous sûr de vouloir supprimer cette photo du spot "${photo.spot_title}" ?`)) {
      return;
    }
    
    try {
      setActionLoading(true);
      await adminAPI.deletePhoto(photo.spot_id, photo.photo_type, photo.photo_index);
      await loadPhotos();
      setSelectedPhoto(null);
    } catch (error) {
      console.error('Failed to delete photo:', error);
      alert('Erreur lors de la suppression');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePhoto = async (photo, updateData) => {
    try {
      setActionLoading(true);
      await adminAPI.updatePhoto(photo.spot_id, photo.photo_type, photo.photo_index, updateData);
      await loadPhotos();
      setEditingPhoto(null);
    } catch (error) {
      console.error('Failed to update photo:', error);
      alert('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMovePhoto = async () => {
    if (!moveTargetSpot || !selectedPhoto) return;
    
    try {
      setActionLoading(true);
      await adminAPI.movePhoto(
        selectedPhoto.spot_id,
        moveTargetSpot,
        selectedPhoto.photo_type,
        selectedPhoto.photo_index
      );
      await loadPhotos();
      setShowMoveModal(false);
      setSelectedPhoto(null);
      setMoveTargetSpot('');
    } catch (error) {
      console.error('Failed to move photo:', error);
      alert('Erreur lors du déplacement');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal d'édition de photo
  const PhotoEditModal = ({ photo, onSave, onClose }) => {
    const [editData, setEditData] = useState({
      url: photo.url || '',
      metadata: {
        date: photo.metadata?.date || '',
        time: photo.metadata?.time || '',
        season: photo.metadata?.season || '',
        orientation: photo.metadata?.orientation || '',
        weather: photo.metadata?.weather || '',
        conditions: photo.metadata?.conditions || ''
      }
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(photo, editData);
    };

    return (
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Modifier la photo
          </DialogTitle>
          <DialogDescription>
            Photo du spot "{photo.spot_title}"
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Aperçu */}
          <div className="flex gap-4">
            <img 
              src={editData.url} 
              alt="Aperçu"
              className="w-40 h-32 object-cover rounded-lg"
              onError={(e) => e.target.src = 'https://via.placeholder.com/160x128?text=Erreur'}
            />
            <div className="flex-1">
              <Label>URL de l'image</Label>
              <Input
                value={editData.url}
                onChange={(e) => setEditData(prev => ({...prev, url: e.target.value}))}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Date
              </Label>
              <Input
                type="date"
                value={editData.metadata.date}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  metadata: {...prev.metadata, date: e.target.value}
                }))}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> Heure
              </Label>
              <Input
                type="time"
                value={editData.metadata.time}
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  metadata: {...prev.metadata, time: e.target.value}
                }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="flex items-center gap-1">
                <Sun className="h-3 w-3" /> Saison
              </Label>
              <Select 
                value={editData.metadata.season} 
                onValueChange={(v) => setEditData(prev => ({
                  ...prev,
                  metadata: {...prev.metadata, season: v}
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Non défini</SelectItem>
                  <SelectItem value="Printemps">Printemps</SelectItem>
                  <SelectItem value="Été">Été</SelectItem>
                  <SelectItem value="Automne">Automne</SelectItem>
                  <SelectItem value="Hiver">Hiver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1">
                <Compass className="h-3 w-3" /> Orientation
              </Label>
              <Select 
                value={editData.metadata.orientation} 
                onValueChange={(v) => setEditData(prev => ({
                  ...prev,
                  metadata: {...prev.metadata, orientation: v}
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Non défini</SelectItem>
                  <SelectItem value="Nord">Nord</SelectItem>
                  <SelectItem value="Nord-Est">Nord-Est</SelectItem>
                  <SelectItem value="Est">Est</SelectItem>
                  <SelectItem value="Sud-Est">Sud-Est</SelectItem>
                  <SelectItem value="Sud">Sud</SelectItem>
                  <SelectItem value="Sud-Ouest">Sud-Ouest</SelectItem>
                  <SelectItem value="Ouest">Ouest</SelectItem>
                  <SelectItem value="Nord-Ouest">Nord-Ouest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1">
              <Cloud className="h-3 w-3" /> Météo / Conditions
            </Label>
            <Input
              value={editData.metadata.weather}
              onChange={(e) => setEditData(prev => ({
                ...prev,
                metadata: {...prev.metadata, weather: e.target.value}
              }))}
              placeholder="Ex: Ensoleillé, brumeux, après la pluie..."
            />
          </div>

          <div>
            <Label>Notes / Conditions spéciales</Label>
            <Input
              value={editData.metadata.conditions}
              onChange={(e) => setEditData(prev => ({
                ...prev,
                metadata: {...prev.metadata, conditions: e.target.value}
              }))}
              placeholder="Ex: Lever de soleil, golden hour..."
            />
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="submit" disabled={actionLoading} className="flex-1">
              <Save className="h-4 w-4 mr-2" />
              {actionLoading ? 'Enregistrement...' : 'Sauvegarder'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Annuler
            </Button>
          </div>
        </form>
      </DialogContent>
    );
  };

  // Modal de déplacement
  const MovePhotoModal = () => (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <ArrowRight className="h-5 w-5" />
          Déplacer la photo
        </DialogTitle>
        <DialogDescription>
          Déplacer cette photo vers un autre spot
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <Label>Photo actuelle</Label>
          <div className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg mt-1">
            <img 
              src={selectedPhoto?.url} 
              alt="" 
              className="w-16 h-12 object-cover rounded"
            />
            <div>
              <p className="font-medium text-sm">{selectedPhoto?.spot_title}</p>
              <p className="text-xs text-gray-500">
                {selectedPhoto?.photo_type === 'parking' ? 'Photo parking' : 'Photo spot'}
              </p>
            </div>
          </div>
        </div>

        <div>
          <Label>Déplacer vers</Label>
          <Select value={moveTargetSpot} onValueChange={setMoveTargetSpot}>
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner un spot" />
            </SelectTrigger>
            <SelectContent>
              {spots
                .filter(s => s.id !== selectedPhoto?.spot_id)
                .map(spot => (
                  <SelectItem key={spot.id} value={spot.id}>
                    {spot.title}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            onClick={handleMovePhoto} 
            disabled={!moveTargetSpot || actionLoading}
            className="flex-1"
          >
            {actionLoading ? 'Déplacement...' : 'Déplacer'}
          </Button>
          <Button variant="outline" onClick={() => setShowMoveModal(false)}>
            Annuler
          </Button>
        </div>
      </div>
    </DialogContent>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestion des Photos</h1>
          <p className="text-gray-600 mt-1">Gérer toutes les photos de la plateforme</p>
        </div>
        <div className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5 text-blue-600" />
          <span className="font-medium text-blue-600">{totalPhotos} photos</span>
        </div>
      </div>

      {/* Filtres */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par titre de spot ou auteur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-full sm:w-48">
              <Select value={photoTypeFilter} onValueChange={setPhotoTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="spot">Photos de spots</SelectItem>
                  <SelectItem value="parking">Photos de parking</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={loadPhotos}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Grille de photos */}
      <Card>
        <CardHeader>
          <CardTitle>Galerie des Photos</CardTitle>
          <CardDescription>
            Cliquez sur une photo pour voir les détails et les options
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Chargement des photos...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {photos.map((photo) => (
                  <div 
                    key={photo.id}
                    className="group relative cursor-pointer"
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <div className="aspect-square overflow-hidden rounded-lg border border-gray-200 hover:border-blue-400 transition-colors">
                      <img 
                        src={photo.url} 
                        alt={photo.spot_title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Erreur'}
                      />
                    </div>
                    
                    {/* Badge type */}
                    <Badge 
                      className={`absolute top-2 left-2 text-xs ${
                        photo.photo_type === 'parking' 
                          ? 'bg-orange-100 text-orange-700' 
                          : 'bg-green-100 text-green-700'
                      }`}
                    >
                      {photo.photo_type === 'parking' ? (
                        <><Car className="h-3 w-3 mr-1" /> Parking</>
                      ) : (
                        <><MapPin className="h-3 w-3 mr-1" /> Spot</>
                      )}
                    </Badge>

                    {/* Overlay avec infos */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col justify-end p-2">
                      <p className="text-white text-xs font-medium truncate">{photo.spot_title}</p>
                      <p className="text-white/70 text-xs truncate">Par {photo.spot_author}</p>
                    </div>
                  </div>
                ))}
              </div>

              {photos.length === 0 && (
                <div className="text-center py-12">
                  <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Aucune photo trouvée</p>
                </div>
              )}

              {/* Scroll infini loader */}
              <div ref={loadMoreRef} className="flex justify-center py-6">
                {loadingMore && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Chargement...</span>
                  </div>
                )}
                {!hasMore && photos.length > 0 && (
                  <p className="text-gray-400 text-sm">{totalPhotos} photos au total</p>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Modal de détail de photo */}
      <Dialog open={!!selectedPhoto && !editingPhoto && !showMoveModal} onOpenChange={() => setSelectedPhoto(null)}>
        {selectedPhoto && (
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Détails de la photo</DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Image */}
              <div>
                <img 
                  src={selectedPhoto.url} 
                  alt={selectedPhoto.spot_title}
                  className="w-full h-64 object-cover rounded-lg"
                />
                <div className="mt-2 flex gap-2">
                  <a 
                    href={selectedPhoto.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                  >
                    <ExternalLink className="h-3 w-3" /> Ouvrir l'image
                  </a>
                </div>
              </div>

              {/* Infos */}
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-500">Spot</Label>
                  <p className="font-medium">{selectedPhoto.spot_title}</p>
                </div>
                
                <div>
                  <Label className="text-gray-500">Auteur du spot</Label>
                  <p>{selectedPhoto.spot_author}</p>
                </div>

                <div>
                  <Label className="text-gray-500">Type</Label>
                  <Badge className={selectedPhoto.photo_type === 'parking' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}>
                    {selectedPhoto.photo_type === 'parking' ? 'Photo parking' : 'Photo spot'}
                  </Badge>
                </div>

                {selectedPhoto.metadata && Object.keys(selectedPhoto.metadata).some(k => selectedPhoto.metadata[k]) && (
                  <div>
                    <Label className="text-gray-500">Métadonnées</Label>
                    <div className="mt-1 space-y-1 text-sm">
                      {selectedPhoto.metadata.date && <p>📅 Date: {selectedPhoto.metadata.date}</p>}
                      {selectedPhoto.metadata.time && <p>🕐 Heure: {selectedPhoto.metadata.time}</p>}
                      {selectedPhoto.metadata.season && <p>🌸 Saison: {selectedPhoto.metadata.season}</p>}
                      {selectedPhoto.metadata.orientation && <p>🧭 Orientation: {selectedPhoto.metadata.orientation}</p>}
                      {selectedPhoto.metadata.weather && <p>☁️ Météo: {selectedPhoto.metadata.weather}</p>}
                      {selectedPhoto.metadata.conditions && <p>📝 Conditions: {selectedPhoto.metadata.conditions}</p>}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <Button size="sm" onClick={() => setEditingPhoto(selectedPhoto)}>
                    <Edit className="h-4 w-4 mr-1" /> Modifier
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowMoveModal(true)}>
                    <ArrowRight className="h-4 w-4 mr-1" /> Déplacer
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive"
                    onClick={() => handleDeletePhoto(selectedPhoto)}
                    disabled={actionLoading}
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Supprimer
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>

      {/* Modal d'édition */}
      <Dialog open={!!editingPhoto} onOpenChange={() => setEditingPhoto(null)}>
        {editingPhoto && (
          <PhotoEditModal 
            photo={editingPhoto} 
            onSave={handleUpdatePhoto}
            onClose={() => setEditingPhoto(null)}
          />
        )}
      </Dialog>

      {/* Modal de déplacement */}
      <Dialog open={showMoveModal} onOpenChange={setShowMoveModal}>
        {showMoveModal && selectedPhoto && <MovePhotoModal />}
      </Dialog>
    </div>
  );
}
